/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';

import config from 'js/app/config';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import { Dialog, breakpoints } from '@coursera/cds-core';
import type { EoiSection } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { ProfilePhone } from 'bundles/account-profile/components/types';
import * as actions from 'bundles/authentication/shared/actions';
import ConfirmationSection from 'bundles/expression-of-interest/components/dcat-eoi-optimizations/ConfirmationSection';
import InitialSection from 'bundles/expression-of-interest/components/dcat-eoi-optimizations/InitialSection';
import PhoneNumberSection from 'bundles/expression-of-interest/components/dcat-eoi-optimizations/PhoneNumberSection';
import SelectSubjectSection from 'bundles/expression-of-interest/components/dcat-eoi-optimizations/SelectSubjectSection';
import SignupSection from 'bundles/expression-of-interest/components/dcat-eoi-optimizations/SignupSection';
import type {
  CreateOrUpdateResponse,
  CreateOrUpdateVariables,
} from 'bundles/expression-of-interest/components/eoi-optimizations/queries/CreateOrUpdateQualificationSurveySectionResponse';
import { CREATE_OR_UPDATE } from 'bundles/expression-of-interest/components/eoi-optimizations/queries/CreateOrUpdateQualificationSurveySectionResponse';
import type {
  PreviewStartSectionResponse,
  PreviewStartSectionVariables,
} from 'bundles/expression-of-interest/components/eoi-optimizations/queries/PreviewStartSection';
import { PREVIEW_START_SECTION } from 'bundles/expression-of-interest/components/eoi-optimizations/queries/PreviewStartSection';
import { ACTION_QUERY_PARAMS } from 'bundles/expression-of-interest/constants';
import {
  clearActionQueryParamLocation,
  getActionQueryParam,
  getDcatEoiVariant,
  getDhubEoiVariant,
  getEoiContext,
  getInputfromUserProfile,
  getResponse,
  getSplitName,
  getVariantHubEoi,
} from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  productId: string;
};

const styles = {
  dialog: css`
    [role='dialog'] {
      max-height: 95vh;

      label {
        margin-bottom: 0;
      }

      ${breakpoints.down('xs')} {
        max-height: unset;
      }
    }
  `,
};

/**
 * Used for both old and new premium-hub DCAT experiment
 */
export const EoiDialog = (props: Props): React.ReactElement => {
  const { isOpen, onClose, onOpen, productId } = props;
  const dcatEoiVariant = getDcatEoiVariant(productId);
  const dhubEoiVariant = getDhubEoiVariant(productId);
  const variantHubEoi = getVariantHubEoi(productId);
  const isLoggedIn = isAuthenticatedUser();

  const track = useTracker();

  // Router variables
  const navigate = useNavigate();
  const location = useLocation();
  const pathnameUrl = location.pathname || '';
  const currentUrl = `${config.url.base}${pathnameUrl.substring(1)}`;
  const eoiContext = getEoiContext(currentUrl);

  // API calls
  const [previewStartSection, { data: previewStartSectionData }] = useMutation<
    PreviewStartSectionResponse,
    PreviewStartSectionVariables
  >(PREVIEW_START_SECTION);
  const sectionId = previewStartSectionData?.QualificationSurveySectionResponse.sectionId || 'section1';
  const [createOrUpdate] = useMutation<CreateOrUpdateResponse, CreateOrUpdateVariables>(CREATE_OR_UPDATE);
  const [responseId, setResponseId] = useState<string>('');

  // Form fields
  const currentUser = userGet();
  const defaultName = getSplitName(currentUser.fullName);
  const [firstName, setFirstName] = useState<string>(defaultName.firstName || '');
  const [lastName, setLastName] = useState<string>(defaultName.lastName || '');
  const [learnerEmail, setLearnerEmail] = useState<string>(currentUser.email_address);
  const [phone, setPhone] = useState<Phone>({ phoneNumber: '' });
  const [subject, setSubject] = useState<{ categoryId: string; categoryText: string } | undefined>();

  // Section management
  type SectionName = 'Initial' | 'PhoneNumber' | 'SelectSubject' | 'Signup' | 'Confirmation';
  const getInitialSection = (): SectionName => {
    if (isLoggedIn && firstName && learnerEmail) {
      if (dcatEoiVariant === 'variantA' || variantHubEoi === 'variantA') {
        return 'Confirmation';
      } else if (dhubEoiVariant === 'variantA') {
        return 'SelectSubject';
      } else if (dcatEoiVariant === 'variantB' || dhubEoiVariant === 'variantB' || variantHubEoi === 'variantB') {
        return 'PhoneNumber';
      }
    }

    return 'Initial';
  };
  const [currentSection, setCurrentSection] = useState<SectionName>(getInitialSection());
  const [isNewSignup, setIsNewSignup] = useState<boolean>(false);
  const [emailAssociatedWithAccount, setEmailAssociatedWithAccount] = useState<boolean>(isLoggedIn);

  // Open the dialog when a user comes from SSO
  useEffect(() => {
    // This can be set from an external link or from a previous login from EOI,
    const actionQueryParam = getActionQueryParam(location);
    if (actionQueryParam === ACTION_QUERY_PARAMS.EMAIL_ME_INFO) {
      onOpen();
      clearActionQueryParamLocation(location, navigate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productId) {
      previewStartSection({ variables: { productId, input: {} } });
    }

    // Get phone number from user profile
    const fetchPhoneNumber = async () => {
      if (isLoggedIn) {
        const phoneResponse = await getInputfromUserProfile();
        let defaultPhone = { phoneNumber: '' } as Phone;
        if (phoneResponse.phoneNumber) {
          defaultPhone = phoneResponse.phoneNumber['org.coursera.survey.question.ShortAnswerResponse'].text as Phone;
        }

        setPhone(defaultPhone as Phone);
      } else {
        setPhone({
          phoneNumber: '',
        });
      }
    };
    fetchPhoneNumber();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Submit EOI for logged in users
  useEffect(() => {
    if (isLoggedIn && isOpen && firstName && learnerEmail && !responseId) {
      const responses = getResponse(firstName, lastName, learnerEmail);

      createOrUpdate({
        variables: {
          productId,
          shouldProcessTerminalActions: true, // send an email the first time an EOI is submitted
          responseId: '', // there is no response ID the first time
          sectionId,
          eoiContext,
          responses,
        },
      }).then((response) => {
        if (response.data) {
          setResponseId(response.data.QualificationSurveySectionResponse.id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, isOpen]);

  const submitResponse = (shouldProcessTerminalActions: boolean): Promise<void | {}> => {
    const responses = getResponse(firstName, lastName, learnerEmail, phone.phoneNumber, subject);

    return createOrUpdate({
      variables: {
        productId,
        shouldProcessTerminalActions,
        responseId: responseId ? `&responseId=${responseId}` : '',
        sectionId,
        eoiContext,
        responses,
      },
    }).then((response) => {
      if (response.data) {
        setResponseId(response.data.QualificationSurveySectionResponse.id);
      }
    });
  };

  const handleClose = (eoiSection: EoiSection) => {
    track('interact_eoi', {
      eoiInteraction: 'close_form',
      eoiSection,
      pageSection: {
        sectionName: 'eoi',
        subindex: 1,
      },
    });

    if (isNewSignup) {
      actions.onSignup();
      setIsNewSignup(false);
    }

    onClose();
  };

  const SectionEventKeys: Record<SectionName, EoiSection> = {
    Initial: 'initial_section',
    PhoneNumber: 'phone_number_section',
    SelectSubject: 'select_subject_section',
    Signup: 'signup_section',
    Confirmation: 'confirmation_section',
  };

  const Sections: Record<SectionName, React.ReactNode> = {
    Initial: (
      <InitialSection
        handleFirstNameChange={(fName: string) => setFirstName(fName)}
        handleLastNameChange={(lName: string) => setLastName(lName)}
        handleEmailChange={(email: string) => setLearnerEmail(email)}
        handlePhoneChange={(data?: ProfilePhone) => {
          if (data?.phone) {
            const phoneNumberForEoiForm = data.phone;
            setPhone(phoneNumberForEoiForm);
          }
        }}
        firstName={firstName}
        lastName={lastName}
        learnerEmail={learnerEmail}
        phone={phone}
        handleSubmit={() => submitResponse(true)}
        navigateSignup={() => setCurrentSection('Signup')}
        navigateConfirmation={() => setCurrentSection('Confirmation')}
        navigateSelectSubject={() => setCurrentSection('SelectSubject')}
        productId={productId}
        setEmailAssociatedWithAccount={(isAssociated: boolean) => setEmailAssociatedWithAccount(isAssociated)}
      />
    ),
    PhoneNumber: (
      <PhoneNumberSection
        phone={phone}
        handleSubmit={() => submitResponse(false)} // an initial EOI is already captured before the phone number section
        navigateSelectSubject={() => setCurrentSection('SelectSubject')}
        navigateConfirmation={() => setCurrentSection('Confirmation')}
        handlePhoneChange={(data?: ProfilePhone) => {
          if (data?.phone) {
            const phoneNumberForEoiForm = data.phone;
            setPhone(phoneNumberForEoiForm);
          }
        }}
        productId={productId}
      />
    ),
    SelectSubject: (
      <SelectSubjectSection
        subject={subject}
        handleSubjectChange={(selection: { categoryId: string; categoryText: string }) => setSubject(selection)}
        handleSubmit={() => submitResponse(false)}
        navigateSignup={() => setCurrentSection('Signup')}
        navigateConfirmation={() => setCurrentSection('Confirmation')}
        emailAssociatedWithAccount={emailAssociatedWithAccount}
      />
    ),
    Signup: (
      <SignupSection
        firstName={firstName}
        lastName={lastName}
        learnerEmail={learnerEmail}
        handleNextSection={(userSignedUp) => {
          setIsNewSignup(userSignedUp);
          setCurrentSection('Confirmation');
        }}
        handleSubmit={() => submitResponse(false)}
      />
    ),
    Confirmation: <ConfirmationSection email={learnerEmail} handleClose={() => handleClose('confirmation_section')} />,
  };

  return (
    <Dialog open={isOpen} onClose={() => handleClose(SectionEventKeys[currentSection])} css={styles.dialog}>
      {Sections[currentSection]}
    </Dialog>
  );
};

export default EoiDialog;
